// src/pages/Admin.tsx
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { Loader2, Shield } from "lucide-react";

/**
 * Admin Page - Protected Route with Security
 * 
 * Features:
 * - Shows login page instead of 404 for unauthenticated admin URLs
 * - Beautiful loading states during auth verification
 * - Auto-redirect logic based on auth status
 * - Session validation
 */
export default function Admin() {
  const { isAuthenticated, isLoading, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle routing based on auth state
  useEffect(() => {
    // Don't do anything while loading
    if (isLoading) return;

    // If authenticated and on /admin root, redirect to dashboard
    if (isAuthenticated && location.pathname === '/admin') {
      navigate('/admin/dashboard', { replace: true });
    }

    // If not authenticated and trying to access protected route, stay on /admin
    if (!isAuthenticated && location.pathname !== '/admin') {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  // Show enhanced loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-turquoise-dark">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="absolute inset-0 bg-teal-500/10 rounded-full blur-3xl animate-pulse" />
            <div className="relative w-24 h-24 bg-gradient-secondary rounded-2xl flex items-center justify-center mx-auto shadow-glow">
              <Shield className="w-12 h-12 text-white animate-pulse" />
            </div>
          </div>

          <Loader2 className="w-10 h-10 animate-spin text-white mx-auto" />

          <div className="space-y-1">
            <p className="text-white font-semibold text-lg">Verifying Authentication</p>
            <p className="text-sm text-muted-foreground">Please wait while we validate your session...</p>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated and has valid token, show dashboard
  if (isAuthenticated && token) {
    return <AdminDashboard />;
  }

  // Not authenticated - show login page
  // This handles ALL unauthenticated admin URL attempts
  return <AdminLogin />;
}